/**
 * Declining a callback request: the reason field and the confirmation.
 *
 * What the route does (POST /callback-requests/{id}/dismiss): the request leaves
 * the queue for good (a dismissed request cannot be reopened from here) and the
 * reason is appended to the request's note for review. It sends the client
 * nothing and logs no interaction, so the confirmation says so: the client asked
 * for a call and will not get one unless somebody reaches out another way.
 */
import { DISMISS_REASON_MAX, dismissReasonProblem } from '../../services/workQueue.service';
import { colors } from '../../styles/theme';

export interface CallbackDismissFormProps {
  readonly requestId: string;
  readonly clientName: string;
  readonly reason: string;
  readonly submitting: boolean;
  /** The server's refusal, shown as it came. */
  readonly error: string | null;
  readonly onReasonChange: (value: string) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export default function CallbackDismissForm({
  requestId,
  clientName,
  reason,
  submitting,
  error,
  onReasonChange,
  onConfirm,
  onCancel,
}: CallbackDismissFormProps): JSX.Element {
  const problem = dismissReasonProblem(reason);
  const inputId = `callback-dismiss-reason-${requestId}`;
  const blocked = submitting || problem !== null;
  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <p style={{ fontSize: 12, color: colors.inkSoft, margin: 0 }}>
        {clientName} asked for a call. If you dismiss this request they will not be told and
        no call will be made unless you reach out another way. The request leaves the queue and
        cannot be reopened from here.
      </p>
      <label htmlFor={inputId} style={{ fontSize: 12, fontWeight: 600, color: colors.inkSoft }}>
        Reason for dismissing
      </label>
      <textarea
        id={inputId}
        value={reason}
        onChange={(e) => onReasonChange(e.target.value)}
        maxLength={DISMISS_REASON_MAX}
        rows={2}
        disabled={submitting}
        style={{
          padding: '6px 10px',
          border: `1px solid ${colors.border}`,
          borderRadius: 6,
          fontSize: 13,
          resize: 'vertical',
        }}
      />
      {problem !== null && !submitting && (
        <p style={{ fontSize: 11, color: colors.muted, margin: 0 }}>{problem}</p>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onConfirm}
          disabled={blocked}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: 'none',
            fontSize: 12,
            fontWeight: 600,
            cursor: blocked ? 'not-allowed' : 'pointer',
            background: blocked ? colors.disabledBg : colors.danger,
            color: blocked ? colors.disabledText : '#FFFFFF',
            whiteSpace: 'nowrap',
          }}
        >
          {submitting ? 'Dismissing…' : 'Dismiss request'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            fontSize: 12,
            fontWeight: 600,
            cursor: submitting ? 'default' : 'pointer',
            background: colors.surface,
            color: colors.inkSoft,
            opacity: submitting ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" style={{ fontSize: 12, color: colors.danger, margin: 0 }}>
          {error}
        </p>
      )}
    </div>
  );
}
